import type { CheckError, GrammarCheckProvider } from '../types/grammar';
import { settingsStore } from '$lib/stores/settings';
import { get } from 'svelte/store';
import {
  buildSubscriptionHeaders,
  getSubscriptionRequestMetadata
} from '$lib/services/subscription/requestMetadata';

export interface BatchJob {
  jobId: string;
  totalChunks: number;
  estimatedTime: number;
  status: 'started' | 'processing' | 'completed' | 'failed';
}

export interface BatchProgress {
  jobId: string;
  currentChunk: number;
  totalChunks: number;
  percentComplete: number;
  status: string;
  message: string;
  estimatedTimeRemaining?: number;
}

export interface BatchResult {
  success: boolean;
  errors?: CheckError[];
  original?: string;
  corrected?: string;
  error?: string;
}

class BatchCheckService {
  private eventSources: Map<string, EventSource> = new Map();
  private progressCallbacks: Map<string, (progress: BatchProgress) => void> = new Map();
  private activeAbortControllers: Map<string, AbortController> = new Map();
  
  /**
   * Determine if text needs batch processing
   * @param text - Text to check
   * @param provider - Provider to use
   * @returns true if batch processing is needed
   */
  needsBatchProcessing(text: string, provider: GrammarCheckProvider): boolean {
    const textLength = text.length;
    
    switch (provider) {
      case 'naraSpeller':
        // NaraSpeller needs batch for texts over 500 characters
        return textLength > 500;
        
      case 'proxy': // Default proxy uses bareun server
      case 'bareun':
      case 'bareunProxy': // Alias for backward compatibility
      case 'premium': // Deprecated: now handled as bareun
        // Bareun can handle up to 4500 chars
        return textLength > 4500;
        
      default:
        return textLength > 4500;
    }
  }
  
  /**
   * Get the appropriate server URL based on provider
   */
  private getServerUrl(provider: GrammarCheckProvider): string {
    // Use the public proxy server for batch processing
    // The proxy server supports batch endpoints
    const serverUrl = 'https://appstore.junlim.org';
    
    
    return serverUrl;
  }
  
  /**
   * Start a batch check job
   * @param text - Text to check
   * @param provider - Provider to use
   * @param apiKey - API key for the provider
   * @param providerOptions - Provider-specific options
   * @returns Batch job information
   */
  async startBatchCheck(
    text: string, 
    provider: GrammarCheckProvider,
    apiKey?: string,
    providerOptions?: any,
    signal?: AbortSignal
  ): Promise<BatchJob> {
    const serverUrl = this.getServerUrl(provider);
    
    if (!serverUrl) {
      throw new Error('Server URL not configured');
    }
    
    const settings = get(settingsStore);
    const options: any = {};
    
    // Add provider-specific options
    if (provider === 'naraSpeller') {
      // Use passed API key first
      if (apiKey) {
        options.apiKey = apiKey;
        options.weakOpt = providerOptions?.weakOpt ?? 0;
      } 
      // Then check if custom server is configured for NaraSpeller
      else if (settings.api?.customServer?.enabled && 
          settings.api.customServer.providerType === 'naraSpeller') {
        options.apiKey = settings.api.customServer.apiKey;
        options.weakOpt = settings.api.customServer.weakOpt ?? 0;
      }
    }
    
    const abortController = new AbortController();
    const requestMetadata = await getSubscriptionRequestMetadata();
    
    // If external signal provided, link it to our controller
    if (signal) {
      signal.addEventListener('abort', () => abortController.abort());
    }
    
    const response = await fetch(`${serverUrl}/api/batch/start`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...buildSubscriptionHeaders(requestMetadata),
      },
      body: JSON.stringify({
        text,
        provider,
        options,
        deviceId: requestMetadata.deviceId,
        tier: requestMetadata.tier,
        licenseKey: requestMetadata.licenseKey || undefined
      }),
      signal: abortController.signal
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to start batch check');
    }
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to start batch check');
    }
    
    // Store abort controller for this job
    this.activeAbortControllers.set(result.jobId, abortController);
    
    return {
      jobId: result.jobId,
      totalChunks: result.totalChunks,
      estimatedTime: result.estimatedTime,
      status: result.status
    };
  }
  
  /**
   * Get batch job status
   * @param jobId - Job ID
   * @param provider - Provider being used
   * @returns Job status
   */
  async getJobStatus(jobId: string, provider: GrammarCheckProvider): Promise<BatchProgress> {
    const serverUrl = this.getServerUrl(provider);
    const requestMetadata = await getSubscriptionRequestMetadata();
    
    const response = await fetch(`${serverUrl}/api/batch/status/${jobId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...buildSubscriptionHeaders(requestMetadata),
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to get job status');
    }
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to get job status');
    }
    
    return {
      jobId: result.id,
      currentChunk: result.currentChunk,
      totalChunks: result.totalChunks,
      percentComplete: result.progress,
      status: result.status,
      message: this.getProgressMessage(result),
      estimatedTimeRemaining: result.estimatedTimeRemaining
    };
  }
  
  /**
   * Get batch job result
   * @param jobId - Job ID
   * @param provider - Provider being used
   * @returns Check result
   */
  async getJobResult(jobId: string, provider: GrammarCheckProvider): Promise<BatchResult> {
    const serverUrl = this.getServerUrl(provider);
    const requestMetadata = await getSubscriptionRequestMetadata();
    
    const response = await fetch(`${serverUrl}/api/batch/result/${jobId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...buildSubscriptionHeaders(requestMetadata),
      }
    });
    
    if (!response.ok) {
      const error = await response.json();
      
      // If job is still processing, return appropriate status
      if (response.status === 202) {
        return {
          success: false,
          error: 'Job is still processing'
        };
      }
      
      throw new Error(error.error || 'Failed to get job result');
    }
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to get job result');
    }
    
    return {
      success: true,
      errors: result.errors,
      original: result.original,
      corrected: result.corrected
    };
  }
  
  /**
   * Subscribe to job progress using Server-Sent Events
   * @param jobId - Job ID
   * @param provider - Provider being used
   * @param onProgress - Callback for progress updates
   * @returns Unsubscribe function
   */
  subscribeToProgress(
    jobId: string,
    provider: GrammarCheckProvider,
    onProgress: (progress: BatchProgress) => void
  ): () => void {
    const serverUrl = this.getServerUrl(provider);
    
    // Close existing connection if any
    this.unsubscribeFromProgress(jobId);
    
    // Create new EventSource
    const eventSource = new EventSource(`${serverUrl}/api/batch/progress/${jobId}`);
    
    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        // Convert to BatchProgress format
        const progress: BatchProgress = {
          jobId: data.jobId || jobId,
          currentChunk: data.currentChunk || 0,
          totalChunks: data.totalChunks || 1,
          percentComplete: data.percentComplete || 0,
          status: data.status || 'processing',
          message: data.message || this.getProgressMessage(data),
          estimatedTimeRemaining: data.estimatedTimeRemaining
        };
        
        onProgress(progress);
        
        // Close connection if job is completed
        if (progress.status === 'completed' || progress.status === 'failed') {
          setTimeout(() => {
            this.unsubscribeFromProgress(jobId);
          }, 100);
        }
      } catch (error) {
        // Failed to parse SSE data
      }
    };
    
    eventSource.onerror = (error) => {
      // SSE connection error
      this.unsubscribeFromProgress(jobId);
      
      // Notify about error
      onProgress({
        jobId,
        currentChunk: 0,
        totalChunks: 1,
        percentComplete: 0,
        status: 'failed',
        message: 'Connection lost. Please check the result manually.',
        estimatedTimeRemaining: 0
      });
    };
    
    // Store EventSource and callback
    this.eventSources.set(jobId, eventSource);
    this.progressCallbacks.set(jobId, onProgress);
    
    // Return unsubscribe function
    return () => this.unsubscribeFromProgress(jobId);
  }
  
  /**
   * Unsubscribe from job progress
   * @param jobId - Job ID
   */
  private unsubscribeFromProgress(jobId: string): void {
    const eventSource = this.eventSources.get(jobId);
    if (eventSource) {
      eventSource.close();
      this.eventSources.delete(jobId);
    }
    this.progressCallbacks.delete(jobId);
    
    // Also clean up abort controller for this job
    const controller = this.activeAbortControllers.get(jobId);
    if (controller) {
      controller.abort();
      this.activeAbortControllers.delete(jobId);
    }
  }
  
  /**
   * Poll for job completion (fallback if SSE not available)
   * @param jobId - Job ID
   * @param provider - Provider being used
   * @param onProgress - Progress callback
   * @param interval - Polling interval in ms
   * @returns Promise that resolves when job completes
   */
  async pollForCompletion(
    jobId: string,
    provider: GrammarCheckProvider,
    onProgress?: (progress: BatchProgress) => void,
    interval: number = 1000
  ): Promise<BatchResult> {
    return new Promise((resolve, reject) => {
      const pollInterval = setInterval(async () => {
        try {
          const status = await this.getJobStatus(jobId, provider);
          
          if (onProgress) {
            onProgress(status);
          }
          
          if (status.status === 'completed') {
            clearInterval(pollInterval);
            const result = await this.getJobResult(jobId, provider);
            resolve(result);
          } else if (status.status === 'failed') {
            clearInterval(pollInterval);
            reject(new Error('Batch job failed'));
          }
        } catch (error) {
          clearInterval(pollInterval);
          reject(error);
        }
      }, interval);
      
      // Set a timeout (5 minutes)
      setTimeout(() => {
        clearInterval(pollInterval);
        reject(new Error('Batch job timed out'));
      }, 300000);
    });
  }
  
  /**
   * Get progress message based on status
   * @param status - Job status object
   * @returns Progress message
   */
  private getProgressMessage(status: any): string {
    if (status.message) {
      return status.message;
    }
    
    if (status.status === 'completed') {
      return '검사가 완료되었습니다.';
    }
    
    if (status.status === 'failed') {
      return '검사 중 오류가 발생했습니다.';
    }
    
    if (status.totalChunks === 1) {
      return '문서를 검사하는 중...';
    }
    
    if (status.currentChunk === 1) {
      return `문서를 ${status.totalChunks}개 부분으로 나누어 검사 중... (1/${status.totalChunks})`;
    }
    
    if (status.currentChunk === status.totalChunks) {
      return `마지막 부분 처리 중... 곧 완료됩니다.`;
    }
    
    return `${status.currentChunk}번째 부분 검사 중... (${status.currentChunk}/${status.totalChunks})`;
  }
  
  /**
   * Cancel all active batch jobs
   */
  cancelAll(): void {
    // Abort all active requests
    for (const [jobId, controller] of this.activeAbortControllers) {
      controller.abort();
    }
    this.activeAbortControllers.clear();
    
    // Close all EventSource connections
    for (const [jobId] of this.eventSources) {
      this.unsubscribeFromProgress(jobId);
    }
    
    // Clear all progress callbacks
    this.progressCallbacks.clear();
  }
  
  /**
   * Clean up all connections
   */
  cleanup(): void {
    this.cancelAll();
  }
}

// Export singleton instance
export const batchCheckService = new BatchCheckService();
